import { useEffect, useRef, useState } from "react";
import type { ReactNode } from "react";

const COARSE = "(pointer: coarse)";

/**
 * Whether the device is used with a finger (iPhone, iPad, Android) rather than a mouse or trackpad.
 * A finger swipes to undo; a mouse gets a button with a second step.
 */
export function useCoarse(): boolean {
  const query = () => typeof matchMedia === "function" && matchMedia(COARSE).matches;
  const [coarse, setCoarse] = useState(query);
  useEffect(() => {
    if (typeof matchMedia !== "function") return;
    const m = matchMedia(COARSE);
    const on = () => setCoarse(m.matches);
    m.addEventListener("change", on);
    return () => m.removeEventListener("change", on);
  }, []);
  return coarse;
}

/**
 * A list item or table row that is undone by swiping it to the right, not by a tap. Past 40 % of
 * the width it calls onSwipe; less snaps back. Red underneath, with the label.
 */
export function SwipeRow({ as = "li", children, onSwipe, off, label, className = "", testid }: { as?: "li" | "tr"; children: ReactNode; onSwipe: () => void; off: boolean; label: string; className?: string; testid?: string }) {
  const [dx, setDx] = useState(0);
  const start = useRef<{ x: number; y: number; w: number; id: number } | null>(null);
  const horizontal = useRef(false);
  const reset = () => {
    start.current = null;
    horizontal.current = false;
    setDx(0);
  };
  const handlers = off
    ? {}
    : {
        onPointerDown: (e: React.PointerEvent<HTMLElement>) => {
          start.current = { x: e.clientX, y: e.clientY, w: e.currentTarget.offsetWidth, id: e.pointerId };
        },
        onPointerMove: (e: React.PointerEvent<HTMLElement>) => {
          const s = start.current;
          if (!s || s.id !== e.pointerId) return;
          const x = e.clientX - s.x;
          if (!horizontal.current) {
            if (Math.abs(e.clientY - s.y) > 12) return reset();
            if (x > 12) {
              horizontal.current = true;
              e.currentTarget.setPointerCapture(e.pointerId);
            }
          }
          if (horizontal.current) setDx(Math.max(0, x));
        },
        onPointerUp: () => {
          if (start.current && dx > start.current.w * 0.4) onSwipe();
          reset();
        },
        onPointerCancel: reset,
      };
  const cls = `${className} swipe${dx > 0 ? " dragging" : ""}${start.current ? " held" : ""}${off ? "" : " can-swipe"}`;
  const style = { "--dx": `${dx}px` } as React.CSSProperties;
  if (as === "tr")
    return (
      <tr className={cls} style={style} data-label={label} data-testid={testid} {...handlers}>
        {children}
      </tr>
    );
  return (
    <li className={cls} style={style} data-testid={testid} {...handlers}>
      {!off && <span className="swipe-under">{label}</span>}
      <span className="swipe-top">{children}</span>
    </li>
  );
}

/** For a mouse: the button asks once more, so a click in passing does nothing. */
export function ConfirmButton({ label, onConfirm, testid }: { label: string; onConfirm: () => unknown; testid?: string }) {
  const [asked, setAsked] = useState(false);
  if (asked)
    return (
      <span className="row confirm">
        <button className="danger" onClick={() => [setAsked(false), onConfirm()]} data-testid={testid ? `${testid}-confirm` : undefined}>
          Wirklich?
        </button>
        <button className="link" onClick={() => setAsked(false)}>
          nein
        </button>
      </span>
    );
  return (
    <button className="link entry-action" onClick={() => setAsked(true)} data-testid={testid}>
      {label}
    </button>
  );
}
